import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

export default class extends Controller {
  static targets = ["container", "item"]
  static values = { url: String }

  connect() {
    this.draggedItem = null
    this.animating = false
    this.boundDragStart = this.handleDragStart.bind(this)
    this.boundDragEnd = this.handleDragEnd.bind(this)
    this.boundDragOver = this.handleDragOver.bind(this)
    this.boundDrop = this.handleDrop.bind(this)

    const container = this.containerTarget
    container.addEventListener("dragstart", this.boundDragStart)
    container.addEventListener("dragend", this.boundDragEnd)
    container.addEventListener("dragover", this.boundDragOver)
    container.addEventListener("drop", this.boundDrop)
  }

  disconnect() {
    const container = this.containerTarget
    container.removeEventListener("dragstart", this.boundDragStart)
    container.removeEventListener("dragend", this.boundDragEnd)
    container.removeEventListener("dragover", this.boundDragOver)
    container.removeEventListener("drop", this.boundDrop)
  }

  findItem(element) {
    return element.closest("[data-image-reorder-target='item']")
  }

  handleDragStart(event) {
    const item = this.findItem(event.target)
    if (!item) return

    this.draggedItem = item
    item.classList.add("opacity-50")
    event.dataTransfer.effectAllowed = "move"

    const img = item.querySelector("img")
    if (img) {
      event.dataTransfer.setDragImage(img, img.width / 2, img.height / 2)
    }
  }

  handleDragEnd() {
    if (this.draggedItem) {
      this.draggedItem.classList.remove("opacity-50")
      this.draggedItem = null
    }
    this.animating = false
    this.saveOrder()
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    if (this.animating) return

    const target = this.findItem(event.target)
    if (!target || !this.draggedItem || this.draggedItem === target) return

    const container = this.containerTarget
    const items = [...container.children]
    const draggedIndex = items.indexOf(this.draggedItem)
    const targetIndex = items.indexOf(target)

    // Capture positions before DOM change
    const rects = new Map()
    for (const item of items) {
      rects.set(item, item.getBoundingClientRect())
    }

    // Move in DOM
    if (draggedIndex < targetIndex) {
      container.insertBefore(this.draggedItem, target.nextSibling)
    } else {
      container.insertBefore(this.draggedItem, target)
    }

    // FLIP animate from old position to new position
    this.animating = true
    let animationCount = 0

    for (const item of container.children) {
      if (item === this.draggedItem) continue

      const oldRect = rects.get(item)
      if (!oldRect) continue

      const newRect = item.getBoundingClientRect()
      const dx = oldRect.left - newRect.left
      const dy = oldRect.top - newRect.top

      if (dx === 0 && dy === 0) continue

      animationCount++
      item.style.transform = `translate(${dx}px, ${dy}px)`
      item.style.transition = "none"

      requestAnimationFrame(() => {
        item.style.transition = "transform 150ms ease"
        item.style.transform = ""
        item.addEventListener("transitionend", () => {
          item.style.transition = ""
          animationCount--
          if (animationCount <= 0) this.animating = false
        }, { once: true })
      })
    }

    if (animationCount === 0) this.animating = false
  }

  handleDrop(event) {
    event.preventDefault()
  }

  saveOrder() {
    const imageIds = this.itemTargets.map(item => item.dataset.imageId)

    patch(this.urlValue, {
      body: JSON.stringify({ image_ids: imageIds }),
      contentType: "application/json"
    })
  }
}
